import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2, FileText, Euro, User, Hash, Mail, Phone, MapPin, CheckCircle2, Download, ExternalLink, Save, BookUser, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import CountrySelect, { useCountries, validateBillingAddress } from "./CountrySelect";

/**
 * Emit a stand-alone Fatura-Recibo OR save it as a draft for later.
 *
 * Modes:
 *   - New invoice (no `initialDraft`): admin starts from scratch.
 *   - Edit draft (`initialDraft` passed): form is prefilled with the
 *     stored values. Saving updates the draft in place; issuing emits
 *     and deletes the draft on success.
 *
 * Drafts allow partial data (e.g. missing NIF). Strict validation runs
 * only when the admin clicks «Emitir Fatura» — it never blocks saving.
 *
 * Customer book: a dropdown at the top lets the admin pick a previously
 * saved client and prefill the fiscal block. A checkbox near the total
 * persists the current customer for reuse on emit.
 */
const isPt = (country) => !country || country === "Portugal";

export default function ManualInvoiceModal({ onClose, onIssued, onDraftSaved, initialDraft = null }) {
  const draftId = initialDraft?.id || null;
  const countries = useCountries();
  const [customer, setCustomer] = useState(() => ({
    name: initialDraft?.customer?.name || "",
    email: initialDraft?.customer?.email || "",
    phone: initialDraft?.customer?.phone || "",
    nif: initialDraft?.customer?.nif || "",
    address: initialDraft?.customer?.address || "",
    city: initialDraft?.customer?.city || "Funchal",
    postal_code: initialDraft?.customer?.postal_code || "9000-000",
    country: initialDraft?.customer?.country || "Portugal",
  }));
  const [lines, setLines] = useState(() => {
    const src = initialDraft?.lines || [];
    if (src.length === 0) return [{ service_title: "", description: "", quantity: 1, unit_price: "" }];
    return src.map((l) => ({
      service_title: l.service_title || "",
      description: l.description || "",
      quantity: l.quantity ?? 1,
      unit_price: l.unit_price ?? "",
    }));
  });
  const [observations, setObservations] = useState(initialDraft?.observations || "");
  const [paymentMethod, setPaymentMethod] = useState(initialDraft?.payment_method || "transferencia");
  // Desconto financeiro opcional aplicado ao total. Aparece no PDF como
  // «Desconto financeiro» e a descrição é acrescentada às observações.
  const [discountValue, setDiscountValue] = useState(
    initialDraft?.discount_value != null ? String(initialDraft.discount_value) : "",
  );
  const [discountNote, setDiscountNote] = useState(initialDraft?.discount_note || "");
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [result, setResult] = useState(null);

  // Customer book — load saved clients for autocomplete-style picker.
  const [book, setBook] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookQuery, setBookQuery] = useState("");
  const [saveCustomerOnEmit, setSaveCustomerOnEmit] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/facturalusa/manual/customers")
      .then(({ data }) => { if (active) setBook(data.items || []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  function pickSavedCustomer(c) {
    setCustomer({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      nif: c.nif || "",
      address: c.address || "",
      city: c.city || (isPt(c.country) ? "Funchal" : ""),
      postal_code: c.postal_code || (isPt(c.country) ? "9000-000" : ""),
      country: c.country || "Portugal",
    });
    setBookOpen(false);
    setBookQuery("");
    toast.success(`Cliente «${c.name}» carregado.`);
  }

  const filteredBook = bookQuery.trim()
    ? book.filter((c) => {
        const q = bookQuery.trim().toLowerCase();
        return (c.name || "").toLowerCase().includes(q)
            || (c.nif || "").toLowerCase().includes(q)
            || (c.email || "").toLowerCase().includes(q);
      })
    : book;

  function setC(field, value) {
    setCustomer((c) => {
      const next = { ...c, [field]: value };
      // País estrangeiro: limpa os valores por defeito portugueses.
      if (field === "country" && value !== "Portugal") {
        if (c.city === "Funchal") next.city = "";
        if (c.postal_code === "9000-000") next.postal_code = "";
      }
      return next;
    });
  }
  function setLine(idx, field, value) {
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }
  function addLine() {
    setLines((arr) => [...arr, { service_title: "", description: "", quantity: 1, unit_price: "" }]);
  }
  function removeLine(idx) {
    setLines((arr) => (arr.length === 1 ? arr : arr.filter((_, i) => i !== idx)));
  }

  const total = lines.reduce((acc, l) => {
    const q = parseFloat(l.quantity) || 0;
    const p = parseFloat(l.unit_price) || 0;
    return acc + q * p;
  }, 0);

  function validate() {
    if (!customer.name.trim()) return "Nome do cliente é obrigatório.";
    const addrErr = validateBillingAddress(customer, countries);
    if (addrErr) return addrErr;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description.trim()) return `Linha ${i + 1}: descrição obrigatória.`;
      const q = parseFloat(l.quantity);
      const p = parseFloat(l.unit_price);
      if (!q || q <= 0) return `Linha ${i + 1}: quantidade deve ser positiva.`;
      if (!p || p <= 0) return `Linha ${i + 1}: preço unitário deve ser positivo.`;
    }
    return null;
  }

  function buildPayload({ strict }) {
    // strict=true => enforce required fields (for emit endpoint).
    // strict=false => allow nulls (for draft save).
    return {
      customer: {
        name: customer.name.trim() || (strict ? "" : null),
        email: customer.email.trim() || null,
        phone: customer.phone.trim() || null,
        nif: customer.nif.trim() || null,
        address: customer.address.trim() || null,
        city: customer.city.trim() || null,
        postal_code: customer.postal_code.trim() || null,
        country: customer.country.trim() || "Portugal",
      },
      lines: lines.map((l) => ({
        service_title: (l.service_title || "").trim() || null,
        description: l.description.trim() || (strict ? "" : null),
        quantity: parseFloat(l.quantity) || (strict ? 0 : null),
        unit_price: parseFloat(l.unit_price) || (strict ? 0 : null),
      })),
      observations: observations.trim() || null,
      payment_method: paymentMethod || null,
      discount_value: parseFloat(discountValue) || 0,
      discount_note: discountNote.trim() || null,
    };
  }

  async function submit() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload({ strict: true });
      // If editing a draft, emit via the dedicated draft-issue endpoint
      // — that path validates + emits + deletes the draft atomically on
      // the server. Otherwise, hit the regular emit endpoint.
      let data;
      if (draftId) {
        // First save the latest edits to the draft, then issue from id
        // (passing save_customer through the query string).
        await api.patch(`/admin/facturalusa/manual/drafts/${draftId}`, payload);
        const { data: issued } = await api.post(
          `/admin/facturalusa/manual/drafts/${draftId}/issue`,
          null,
          { params: { save_customer: saveCustomerOnEmit } },
        );
        data = issued;
      } else {
        const res = await api.post("/admin/facturalusa/manual", {
          ...payload,
          save_customer: saveCustomerOnEmit,
        });
        data = res.data;
      }
      setResult(data);
      toast.success(`Fatura ${data.invoice_number || ""} emitida com sucesso.${saveCustomerOnEmit ? " Cliente guardado para reutilização." : ""}`);
      onIssued?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function saveDraft() {
    setSavingDraft(true);
    try {
      const payload = buildPayload({ strict: false });
      if (draftId) {
        await api.patch(`/admin/facturalusa/manual/drafts/${draftId}`, payload);
        toast.success("Rascunho atualizado.");
      } else {
        await api.post("/admin/facturalusa/manual/drafts", payload);
        toast.success("Rascunho guardado. Pode continuar mais tarde.");
      }
      onDraftSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDraft(false);
    }
  }

  function downloadPdf() {
    if (!result?.invoice_id) return;
    const a = document.createElement("a");
    a.href = `${api.defaults.baseURL}/admin/facturalusa/manual/${result.invoice_id}/pdf`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${result.invoice_number || "factura"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
         data-testid="manual-invoice-modal">
      <div className="bg-emerald-950 border border-emerald-300/20 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl my-0 sm:my-8 max-h-[95vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-emerald-300/15 sticky top-0 bg-emerald-950 z-10">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-amber-300/15 p-2.5">
              <FileText size={20} className="text-amber-300" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">
                {result ? "Fatura emitida com sucesso" : (draftId ? "Editar Rascunho" : "Nova Fatura")}
              </h2>
              <p className="text-xs text-emerald-200/70 mt-0.5">
                {result
                  ? "O cliente recebeu o PDF por email. Pode também descarregá-lo abaixo."
                  : (draftId
                      ? "Continue a preencher os dados em falta e emita quando estiver pronto."
                      : "Emitir Fatura-Recibo com dados fiscais do cliente.")}
              </p>
            </div>
          </div>
          <button onClick={onClose}
                  className="text-emerald-200/60 hover:text-white p-1"
                  data-testid="manual-invoice-close">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {result ? (
            <SuccessView result={result} onDownload={downloadPdf} />
          ) : (
            <>
              {/* Customer book picker — appears only when there are
                  saved customers. Lets the admin reuse fiscal data from
                  a previous invoice instead of re-typing it. */}
              {book.length > 0 && (
                <section className="rounded-xl border border-emerald-300/20 bg-emerald-300/5 p-3" data-testid="mi-book-section">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-emerald-200 text-sm font-semibold">
                      <BookUser size={14} className="text-amber-300" />
                      Carregar cliente guardado
                      <span className="text-[10px] rounded-full bg-emerald-400/15 text-emerald-200 px-2 py-0.5 font-mono">
                        {book.length}
                      </span>
                    </div>
                    <button onClick={() => setBookOpen(!bookOpen)}
                            className="text-xs rounded-lg border border-amber-300/30 bg-amber-300/10 text-amber-200 hover:bg-amber-300/15 px-3 py-1.5 inline-flex items-center gap-1.5"
                            data-testid="mi-book-toggle">
                      {bookOpen ? "Fechar" : "Procurar cliente"}
                    </button>
                  </div>
                  {bookOpen && (
                    <div className="mt-3 space-y-2" data-testid="mi-book-panel">
                      <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-200/40" />
                        <input
                          type="text"
                          value={bookQuery}
                          onChange={(e) => setBookQuery(e.target.value)}
                          placeholder="Pesquisar por nome, NIF ou email"
                          className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm pl-9 pr-3 py-2 focus:outline-none focus:border-amber-300/50"
                          data-testid="mi-book-search"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto rounded-lg border border-emerald-300/10 divide-y divide-emerald-300/10">
                        {filteredBook.length === 0 ? (
                          <p className="p-3 text-xs text-emerald-200/60 text-center">Sem resultados.</p>
                        ) : filteredBook.map((c) => (
                          <button key={c.id}
                                  onClick={() => pickSavedCustomer(c)}
                                  className="w-full text-left p-3 hover:bg-emerald-300/10 transition group"
                                  data-testid={`mi-book-pick-${c.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-white text-sm font-semibold truncate">{c.name}</span>
                              {c.nif && c.nif !== "999999990" && (
                                <span className="text-[10px] font-mono text-emerald-200/60">NIF {c.nif}</span>
                              )}
                            </div>
                            <p className="text-[11px] text-emerald-200/50 truncate mt-0.5">
                              {[c.email, c.phone, c.city].filter(Boolean).join(" · ") || "—"}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Customer block */}
              <section>
                <h3 className="text-sm font-semibold text-amber-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <User size={14} /> Dados fiscais do cliente
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Nome / Razão social *" value={customer.name} onChange={(v) => setC("name", v)}
                         placeholder="Ex: João Silva ou ACME Lda" testid="mi-name" required />
                  <Field label="NIF" value={customer.nif} onChange={(v) => setC("nif", v)}
                         placeholder="Opcional — vazio = consumidor final" testid="mi-nif"
                         icon={<Hash size={12} />} />
                  <Field label="Email" value={customer.email} onChange={(v) => setC("email", v)}
                         placeholder="cliente@email.com" testid="mi-email" type="email"
                         icon={<Mail size={12} />} />
                  <Field label="Telefone" value={customer.phone} onChange={(v) => setC("phone", v)}
                         placeholder="+351 9XX XXX XXX" testid="mi-phone"
                         icon={<Phone size={12} />} />
                  <Field label="Morada" value={customer.address} onChange={(v) => setC("address", v)}
                         placeholder="Rua, número, andar" testid="mi-address"
                         icon={<MapPin size={12} />} className="sm:col-span-2" />
                  <Field label="Cidade" value={customer.city} onChange={(v) => setC("city", v)}
                         testid="mi-city" />
                  <Field label="Código postal" value={customer.postal_code}
                         onChange={(v) => setC("postal_code", v)}
                         testid="mi-postal" placeholder="9000-000" />
                  <div className="sm:col-span-2">
                    <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1">País *</label>
                    <CountrySelect value={customer.country} onChange={(v) => setC("country", v)} testId="mi-country"
                                   className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50" />
                  </div>
                </div>
              </section>

              {/* Lines */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                    <FileText size={14} /> Linhas da fatura
                  </h3>
                  <button onClick={addLine}
                          className="text-xs rounded-lg border border-emerald-300/30 bg-emerald-300/10 px-3 py-1.5 text-emerald-100 hover:bg-emerald-300/20 inline-flex items-center gap-1.5"
                          data-testid="mi-add-line">
                    <Plus size={12} /> Nova linha
                  </button>
                </div>
                <div className="space-y-3">
                  {lines.map((l, idx) => (
                    <div key={idx} className="rounded-xl border border-emerald-300/15 bg-emerald-900/30 p-3 space-y-2"
                         data-testid={`mi-line-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-emerald-200/60 font-semibold">
                          Linha {idx + 1}
                        </span>
                        {lines.length > 1 && (
                          <button onClick={() => removeLine(idx)}
                                  className="text-red-300 hover:text-red-200 text-xs inline-flex items-center gap-1"
                                  data-testid={`mi-remove-line-${idx}`}>
                            <Trash2 size={12} /> Remover
                          </button>
                        )}
                      </div>
                      <ServiceTitleField
                        value={l.service_title}
                        onChange={(v) => setLine(idx, "service_title", v)}
                        testid={`mi-line-title-${idx}`}
                      />
                      <DescriptionField value={l.description}
                                        onChange={(v) => setLine(idx, "description", v)}
                                        testid={`mi-line-desc-${idx}`} />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Quantidade *" value={l.quantity}
                               onChange={(v) => setLine(idx, "quantity", v)}
                               type="number" step="0.01" min="0.01"
                               testid={`mi-line-qty-${idx}`} />
                        <Field label="Preço unitário (€) *" value={l.unit_price}
                               onChange={(v) => setLine(idx, "unit_price", v)}
                               type="number" step="0.01" min="0.01"
                               placeholder="0.00"
                               testid={`mi-line-price-${idx}`}
                               icon={<Euro size={12} />} />
                      </div>
                      <div className="text-right text-xs text-emerald-200/70">
                        Subtotal:{" "}
                        <span className="text-amber-300 font-mono font-bold">
                          {((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Payment + observations */}
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1">
                    Método de pagamento
                  </label>
                  <select value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                          data-testid="mi-payment-method">
                    <option value="transferencia">Transferência bancária</option>
                    <option value="mbway">MB WAY</option>
                    <option value="multibanco">Multibanco</option>
                    <option value="cash">Numerário</option>
                    <option value="card">Cartão</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1">
                    Observações (opcional)
                  </label>
                  <input type="text" value={observations}
                         onChange={(e) => setObservations(e.target.value)}
                         placeholder="Notas que aparecerão na fatura"
                         className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                         data-testid="mi-observations" />
                </div>
              </section>

              {/* Discount (optional) */}
              <section className="rounded-xl border border-amber-300/20 bg-emerald-900/40 p-4 space-y-3" data-testid="mi-discount-section">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold">Desconto financeiro (opcional)</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] text-emerald-200/50 block mb-1">Valor (€)</label>
                    <input type="number" step="0.01" min="0" value={discountValue}
                           onChange={(e) => setDiscountValue(e.target.value)}
                           placeholder="0.00"
                           className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                           data-testid="mi-discount-value" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[11px] text-emerald-200/50 block mb-1">Motivo do desconto</label>
                    <input type="text" maxLength={200} value={discountNote}
                           onChange={(e) => setDiscountNote(e.target.value)}
                           placeholder="Ex: Cliente fiel · Desconto de campanha · Pagamento adiantado…"
                           className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                           data-testid="mi-discount-note" />
                  </div>
                </div>
                <p className="text-[10px] text-emerald-200/50 leading-snug">
                  O valor é subtraído ao total no PDF (linha «Desconto financeiro») e o motivo aparece nas observações da fatura.
                </p>
              </section>

              {/* Total */}
              <section className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-4"
                       data-testid="mi-total-section">
                <div className="flex items-center justify-between">
                  <span className="text-amber-200 text-sm font-semibold uppercase tracking-wider">
                    Total a faturar
                  </span>
                  <span className="text-2xl sm:text-3xl font-bold text-amber-300 font-mono"
                        data-testid="mi-total">
                    {Math.max(0, total - (parseFloat(discountValue) || 0)).toFixed(2)}€
                  </span>
                </div>
                {parseFloat(discountValue) > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-300/20 flex justify-between text-[11px] text-amber-200/70">
                    <span>Subtotal: {total.toFixed(2)}€</span>
                    <span>Desconto: −{(parseFloat(discountValue) || 0).toFixed(2)}€</span>
                  </div>
                )}
              </section>

              {/* Save customer for future reuse */}
              <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-emerald-300/15 bg-emerald-900/30 p-3 hover:bg-emerald-900/50 transition"
                     data-testid="mi-save-customer-wrapper">
                <input type="checkbox"
                       checked={saveCustomerOnEmit}
                       onChange={(e) => setSaveCustomerOnEmit(e.target.checked)}
                       className="mt-0.5 w-4 h-4 rounded border-emerald-300/40 bg-emerald-900 text-amber-400 focus:ring-amber-300/50 cursor-pointer"
                       data-testid="mi-save-customer-checkbox" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-white font-medium inline-flex items-center gap-1.5">
                    <BookUser size={14} className="text-amber-300" />
                    Guardar dados deste cliente para reutilizar
                  </span>
                  <p className="text-[11px] text-emerald-200/60 mt-0.5">
                    Cliente fica no livro para futuras faturas/limpezas. Dedup automático por NIF ou email.
                  </p>
                </div>
              </label>

              <p className="text-[11px] text-emerald-200/60 leading-relaxed">
                💡 Esta fatura é emitida como Fatura-Recibo (valor já recebido).
                IVA isento ao abrigo do art. 53.º do CIVA. O PDF será enviado por
                email ao cliente caso indique um endereço de email.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-emerald-300/15 p-4 sm:p-5 flex flex-col sm:flex-row gap-2 sm:justify-end sticky bottom-0 bg-emerald-950">
          {result ? (
            <>
              <button onClick={onClose}
                      className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-5 py-2.5 text-sm text-emerald-100 hover:bg-emerald-300/10"
                      data-testid="mi-done">
                Fechar
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose}
                      disabled={submitting || savingDraft}
                      className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-5 py-2.5 text-sm text-emerald-100 hover:bg-emerald-300/10 disabled:opacity-50"
                      data-testid="mi-cancel">
                Cancelar
              </button>
              <button onClick={saveDraft}
                      disabled={submitting || savingDraft}
                      className="rounded-lg border border-emerald-300/40 bg-emerald-300/10 px-5 py-2.5 text-sm font-semibold text-emerald-100 hover:bg-emerald-300/20 disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                      data-testid="mi-save-draft"
                      title="Guardar para continuar mais tarde">
                {savingDraft ? "A guardar…" : <><Save size={14} /> Guardar Rascunho</>}
              </button>
              <button onClick={submit}
                      disabled={submitting || savingDraft || total <= 0}
                      className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-5 py-2.5 text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                      data-testid="mi-submit">
                {submitting ? "A emitir…" : <><FileText size={14} /> Emitir Fatura</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const DESC_MAX = 255; // limite Facturalusa (items[].details)

/** Multi-line description (paste-friendly) that grows with its content. */
function DescriptionField({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [value]);
  const len = (value || "").length;
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1">Descrição *</label>
      <textarea
        ref={ref}
        rows={3}
        value={value}
        maxLength={DESC_MAX}
        onChange={(e) => onChange(e.target.value.replace(/\r\n?/g, "\n"))}
        placeholder={"Ex: Apartamento Mar 302\n22/06 — limpeza check-out\n28/06 — limpeza check-out"}
        className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 leading-relaxed resize-y min-h-[84px] focus:outline-none focus:border-amber-300/50 placeholder:text-emerald-200/30"
        data-testid={testid}
      />
      <div className="flex justify-between gap-3 text-[11px] mt-1 text-emerald-200/50">
        <span>Pode colar texto com várias linhas (ex.: várias datas).</span>
        <span className={len > DESC_MAX - 20 ? "text-amber-300" : ""} data-testid={`${testid}-count`}>{len}/{DESC_MAX}</span>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, type = "text", step, min, icon, required, helper, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </label>
      <input
        type={type}
        step={step}
        min={min}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50 placeholder:text-emerald-200/30"
        data-testid={testid}
      />
      {helper && (
        <p className="text-[10px] text-emerald-200/50 mt-1 leading-snug">{helper}</p>
      )}
    </div>
  );
}

/**
 * Service-title picker with a preset dropdown and a "Personalizado" mode.
 *
 * Common cases are one-tap on a select; when the admin needs a bespoke
 * title («Serviço de canalização», «Transporte», etc.) they pick
 * "Personalizado..." and type it in the revealed input. Empty string
 * ≡ default "Serviço de limpeza" (kept for backwards compat).
 */
const SERVICE_PRESETS = [
  "Serviço de Limpeza",
  "Serviço de Lavandaria",
  "Serviço de Manutenção",
];

function ServiceTitleField({ value, onChange, testid }) {
  const isPreset = !value || SERVICE_PRESETS.some((p) => p.toLowerCase() === (value || "").toLowerCase());
  const [mode, setMode] = React.useState(isPreset ? "preset" : "custom");
  const currentPreset = isPreset ? (value || SERVICE_PRESETS[0]) : SERVICE_PRESETS[0];

  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold block mb-1">
        Tipo de serviço
      </label>
      {mode === "preset" ? (
        <select
          value={currentPreset}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") {
              setMode("custom");
              onChange("");
            } else {
              onChange(v);
            }
          }}
          className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
          data-testid={testid}
        >
          {SERVICE_PRESETS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
          <option value="__custom__">✎ Personalizado…</option>
        </select>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Ex: Serviço de canalização · Transporte…"
            autoFocus
            className="flex-1 rounded-lg bg-emerald-900/50 border border-amber-300/40 text-white text-base sm:text-sm px-3 py-2 focus:outline-none focus:border-amber-300/70 placeholder:text-emerald-200/30"
            data-testid={`${testid}-custom`}
          />
          <button
            type="button"
            onClick={() => { setMode("preset"); onChange(""); }}
            className="text-xs px-3 py-2 rounded-lg border border-emerald-300/20 text-emerald-200/70 hover:bg-emerald-300/5"
            title="Voltar à lista de tipos predefinidos"
          >
            ↩
          </button>
        </div>
      )}
      <p className="text-[10px] text-emerald-200/50 mt-1 leading-snug">
        Aparece como cabeçalho da linha no PDF. Escolha um preset ou «Personalizado» para escrever à mão.
      </p>
    </div>
  );
}


function SuccessView({ result, onDownload }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-400/10 p-5 flex items-start gap-3">
        <CheckCircle2 size={28} className="text-emerald-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold">Fatura criada na Facturalusa</p>
          <p className="text-amber-300 font-mono text-lg font-bold mt-1">
            {result.invoice_number || `#${result.invoice_id}`}
          </p>
          <div className="mt-2 text-xs text-emerald-100 space-y-1">
            <div><span className="text-emerald-200/60">Cliente:</span> {result.customer_name}</div>
            {result.customer_email && (
              <div><span className="text-emerald-200/60">Email enviado para:</span> {result.customer_email}</div>
            )}
            <div><span className="text-emerald-200/60">Total:</span>{" "}
              <span className="font-bold">{(result.amount || 0).toFixed(2)}€</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={onDownload}
                className="flex-1 rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2 justify-center"
                data-testid="mi-download-pdf">
          <Download size={14} /> Descarregar PDF
        </button>
        {result.invoice_id && (
          <a href={`${api.defaults.baseURL}/admin/facturalusa/manual/${result.invoice_id}/pdf?inline=true`}
             target="_blank" rel="noreferrer"
             className="flex-1 rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-4 py-2.5 text-sm text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-2 justify-center"
             data-testid="mi-view-pdf">
            <ExternalLink size={14} /> Abrir PDF num separador
          </a>
        )}
      </div>
    </div>
  );
}
